/**
 * Runtime Logging: Structured Logging System
 *
 * Provides standardized, structured logging across the platform.
 * Supports semantic log events, severity levels, and execution metadata.
 *
 * @platform-infrastructure
 */

import { RuntimeContext, RuntimeExecutionId, CorrelationId } from '../contracts'

/**
 * Log Level - Severity levels for logging
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

/**
 * Log Category - Semantic categories for log events
 */
export enum LogCategory {
  // Runtime categories
  RUNTIME_EXECUTION = 'runtime.execution',
  RUNTIME_REQUEST = 'runtime.request',
  RUNTIME_ERROR = 'runtime.error',
  RUNTIME_TRACE = 'runtime.trace',

  // Application categories
  APPLICATION_SERVICE = 'application.service',
  APPLICATION_VALIDATION = 'application.validation',

  // Domain categories
  DOMAIN_ADAPTER = 'domain.adapter',
  DOMAIN_VALIDATION = 'domain.validation',

  // Gateway categories
  GATEWAY_REQUEST = 'gateway.request',
  GATEWAY_RESPONSE = 'gateway.response',
  GATEWAY_ERROR = 'gateway.error',

  // Presentation categories
  PRESENTATION_COMPONENT = 'presentation.component',
  PRESENTATION_HOOK = 'presentation.hook',
  PRESENTATION_USER_ACTION = 'presentation.user_action',

  // System categories
  SYSTEM_AUTH = 'system.auth',
  SYSTEM_CONFIG = 'system.config',
  SYSTEM_PERFORMANCE = 'system.performance',
}

/**
 * Structured Log Entry
 */
export interface LogEntry {
  /** Timestamp */
  timestamp: Date

  /** Log level */
  level: LogLevel

  /** Log category */
  category: LogCategory

  /** Log message */
  message: string

  /** Execution context */
  context?: RuntimeContext

  /** Additional data */
  data?: Record<string, any>

  /** Error details */
  error?: Error

  /** Performance metrics */
  metrics?: {
    duration?: number
    memoryUsage?: number
    networkRequests?: number
  }
}

/**
 * Runtime Logger
 *
 * Structured logging system with semantic categories and execution context.
 */
export class RuntimeLogger {
  private static logLevel: LogLevel = LogLevel.INFO
  private static logHandlers: Array<(entry: LogEntry) => void> = []

  /**
   * Set global log level
   */
  static setLogLevel(level: LogLevel): void {
    this.logLevel = level
  }

  /**
   * Add a log handler
   */
  static addHandler(handler: (entry: LogEntry) => void): void {
    this.logHandlers.push(handler)
  }

  /**
   * Remove a log handler
   */
  static removeHandler(handler: (entry: LogEntry) => void): void {
    this.logHandlers = this.logHandlers.filter(h => h !== handler)
  }

  /**
   * Create a log entry
   */
  private static createEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>,
    error?: Error,
    metrics?: LogEntry['metrics']
  ): LogEntry {
    return {
      timestamp: new Date(),
      level,
      category,
      message,
      context,
      data,
      error,
      metrics,
    }
  }

  /**
   * Log an entry
   */
  private static log(entry: LogEntry): void {
    // Check log level
    const levelPriority = {
      [LogLevel.DEBUG]: 0,
      [LogLevel.INFO]: 1,
      [LogLevel.WARN]: 2,
      [LogLevel.ERROR]: 3,
      [LogLevel.FATAL]: 4,
    }

    if (levelPriority[entry.level] < levelPriority[this.logLevel]) {
      return
    }

    // Default console handler
    this.defaultConsoleHandler(entry)

    // Custom handlers
    this.logHandlers.forEach(handler => {
      try {
        handler(entry)
      } catch (error) {
        console.error('Log handler error:', error)
      }
    })
  }

  /**
   * Default console handler
   */
  private static defaultConsoleHandler(entry: LogEntry): void {
    const prefix = `[${entry.timestamp.toISOString()}] [${entry.level.toUpperCase()}] [${entry.category}]`

    const contextInfo = entry.context
      ? ` [${entry.context.executionId}:${entry.context.correlationId}]`
      : ''

    const message = `${prefix}${contextInfo} ${entry.message}`

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(message, entry.data, entry.error)
        break
      case LogLevel.INFO:
        console.info(message, entry.data)
        break
      case LogLevel.WARN:
        console.warn(message, entry.data, entry.error)
        break
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(message, entry.data, entry.error)
        break
    }
  }

  // Public logging methods

  static debug(
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.log(this.createEntry(LogLevel.DEBUG, category, message, context, data))
  }

  static info(
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.log(this.createEntry(LogLevel.INFO, category, message, context, data))
  }

  static warn(
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>,
    error?: Error
  ): void {
    this.log(this.createEntry(LogLevel.WARN, category, message, context, data, error))
  }

  static error(
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>,
    error?: Error
  ): void {
    this.log(this.createEntry(LogLevel.ERROR, category, message, context, data, error))
  }

  static fatal(
    category: LogCategory,
    message: string,
    context?: RuntimeContext,
    data?: Record<string, any>,
    error?: Error
  ): void {
    this.log(this.createEntry(LogLevel.FATAL, category, message, context, data, error))
  }

  // Semantic logging methods

  static executionStart(
    operation: string,
    context: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.info(
      LogCategory.RUNTIME_EXECUTION,
      `Execution started: ${operation}`,
      context,
      { operation, ...data }
    )
  }

  static executionComplete(
    operation: string,
    context: RuntimeContext,
    duration: number,
    data?: Record<string, any>
  ): void {
    this.info(
      LogCategory.RUNTIME_EXECUTION,
      `Execution completed: ${operation}`,
      context,
      { operation, duration, ...data }
    )
  }

  static executionError(
    operation: string,
    context: RuntimeContext,
    error: Error,
    data?: Record<string, any>
  ): void {
    this.error(
      LogCategory.RUNTIME_EXECUTION,
      `Execution failed: ${operation}`,
      context,
      { operation, ...data },
      error
    )
  }

  static requestStart(
    method: string,
    url: string,
    context: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.info(
      LogCategory.RUNTIME_REQUEST,
      `Request started: ${method} ${url}`,
      context,
      { method, url, ...data }
    )
  }

  static requestComplete(
    method: string,
    url: string,
    statusCode: number,
    duration: number,
    context: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.info(
      LogCategory.RUNTIME_REQUEST,
      `Request completed: ${method} ${url} → ${statusCode}`,
      context,
      { method, url, statusCode, duration, ...data }
    )
  }

  static requestError(
    method: string,
    url: string,
    error: Error,
    context: RuntimeContext,
    data?: Record<string, any>
  ): void {
    this.error(
      LogCategory.RUNTIME_REQUEST,
      `Request failed: ${method} ${url}`,
      context,
      { method, url, ...data },
      error
    )
  }
}